import isEmpty from 'lodash/isEmpty';
import { ICohortRow } from '../../../redux/slices/oldBuilder';
import {
  ExpressionCohortOperatorKey,
  MeasureCohortOperatorKey,
  PerformedCohortOperatorKey,
  RelationalOperatorKey,
  RetentionCohortOperatorKey,
  SimpleCohortTypeKey,
  TimeToConvertOperatorKey,
  TimeWindowKey,
} from '../../apollo/graphqlSchema';
import { isValidNumberWithinRange } from '../../numbers';
import { isEmptyEventCondition } from '../events';
import { getBasicMeasureRowErrors } from '../validation';
import { isNil } from 'lodash';
import { zRetentionCohortOperatorKey } from '../../apollo/zodSchema';
import {
  isFunnelLookForwardWindowInputRequired,
  isRetentionLookForwardWindowInputRequired,
} from '../../../redux/slices/oldBuilder/utils';
import { UnreachableCaseError } from 'ts-essentials';

const getMeasureCohortRowErrors = (cohortRow: ICohortRow) => {
  const errors: any = {};

  if (!isValidNumberWithinRange(cohortRow.operatorValues[0])) {
    errors.operatorValues = [true, false];
  }

  if (
    cohortRow.operator === MeasureCohortOperatorKey.Between &&
    !isValidNumberWithinRange(cohortRow.operatorValues[1])
  ) {
    errors.operatorValues = [!!errors.operatorValues, true];
  }

  // In case of named measure, we will finish validation at this point
  if (cohortRow?.measureId) {
    return errors;
  }

  const basicMeasureErrors = getBasicMeasureRowErrors(cohortRow);

  return {
    ...errors,
    ...basicMeasureErrors,
  };
};

const getRetentionCohortRowErrors = (cohortRow: ICohortRow) => {
  const errors: any = {};

  if (!cohortRow?.analysisId) {
    errors.analysisId = true;
  }

  const {
    operator,
    operatorValues,
    returnedOperator,
    lookForwardWindow,
    cohortDurationStart,
    datePicker,
  } = cohortRow;

  if (
    isRetentionLookForwardWindowInputRequired(
      lookForwardWindow,
      zRetentionCohortOperatorKey.parse(operator),
      !isNil(datePicker),
    ) &&
    !isValidNumberWithinRange(lookForwardWindow.windowLength)
  ) {
    errors.lookForwardWindow = true;
  }

  if (
    !isNil(datePicker) &&
    cohortDurationStart.required &&
    isNil(cohortDurationStart.date)
  ) {
    errors.cohortDurationStart = true;
  }

  if (
    operator === RetentionCohortOperatorKey.Retained ||
    operator === RetentionCohortOperatorKey.NotRetained
  ) {
    return errors;
  }

  if (!isValidNumberWithinRange(operatorValues[0])) {
    errors.operatorValues = [true, false];
  }

  if (
    (operator === RetentionCohortOperatorKey.RetainedBetween ||
      returnedOperator === RelationalOperatorKey.Between) &&
    !isValidNumberWithinRange(operatorValues[1])
  ) {
    errors.operatorValues = [!!errors.operatorValues, true];
  }

  return errors;
};

const getFunnelCohortRowErrors = (cohortRow: ICohortRow) => {
  const errors: any = {};

  if (!cohortRow?.analysisId) {
    errors.analysisId = true;
  }

  const {
    lookForwardWindow,
    operatorValues,
    datePicker,
    cohortDurationStart,
    timeToConvert,
  } = cohortRow;

  if (!timeToConvert && !isValidNumberWithinRange(operatorValues[0])) {
    errors.operatorValues = [true, false];
  }

  if (timeToConvert) {
    if (!isValidNumberWithinRange(timeToConvert.operatorValues[0])) {
      errors.ttcOperatorValues = [true, false];
    }

    if (
      timeToConvert.operator === TimeToConvertOperatorKey.Between &&
      !isValidNumberWithinRange(timeToConvert.operatorValues[1])
    ) {
      errors.ttcOperatorValues = [!!errors.ttcOperatorValues, true];
    }
  }

  if (
    !isNil(datePicker) &&
    cohortDurationStart.required &&
    isNil(cohortDurationStart.date)
  ) {
    errors.cohortDurationStart = true;
  }

  if (
    isFunnelLookForwardWindowInputRequired(
      lookForwardWindow,
      !isNil(datePicker),
    ) &&
    !isValidNumberWithinRange(lookForwardWindow.windowLength)
  ) {
    errors.lookForwardWindow = true;
  }

  return errors;
};

const getPerformedCohortRowErrors = (cohortRow: ICohortRow) => {
  const errors: any = {};

  cohortRow.eventConditions.forEach((eventCondition, eventConditionIndex) => {
    if (isEmptyEventCondition(eventCondition)) {
      errors.events = {
        ...(errors?.events || {}),
        [eventConditionIndex]: true,
      };
    }
  });

  const { operator, operatorValues } = cohortRow;

  if (
    operator === PerformedCohortOperatorKey.True ||
    operator === PerformedCohortOperatorKey.False
  ) {
    return errors;
  }
  if (!isValidNumberWithinRange(operatorValues[0])) {
    errors.operatorValues = [true, false];
  }

  if (
    operator === PerformedCohortOperatorKey.Between &&
    !isValidNumberWithinRange(operatorValues[1])
  ) {
    errors.operatorValues = [!!errors.operatorValues, true];
  }

  return errors;
};

const getExpressionCohortRowErrors = (cohortRow: ICohortRow) => {
  const errors: any = {};

  if (!cohortRow.analysisId) {
    errors.analysisId = true;
  }
  if (!isValidNumberWithinRange(cohortRow.operatorValues[0])) {
    errors.operatorValues = [true, false];
  }

  if (
    cohortRow.operator === ExpressionCohortOperatorKey.Between &&
    !isValidNumberWithinRange(cohortRow.operatorValues[1])
  ) {
    errors.operatorValues = [!!errors.operatorValues, true];
  }

  return errors;
};

export const getCohortRowErrors = (cohortRow: ICohortRow) => {
  const {
    isActive,
    isCompoundCohort,
    simpleCohortTypeKey,
    timeWindow,
    timeWindowValues,
  } = cohortRow;

  if (!isActive || isCompoundCohort) {
    return {};
  }

  const errors: any = {};

  if (
    timeWindow &&
    timeWindow !== TimeWindowKey.Overall &&
    timeWindow !== TimeWindowKey.Current &&
    timeWindow !== TimeWindowKey.InEach
  ) {
    if (!isValidNumberWithinRange(timeWindowValues?.[0])) {
      errors.timeWindowValues = [true, false];
    }

    if (
      (timeWindow === TimeWindowKey.RollingBackBetween ||
        timeWindow === TimeWindowKey.BetweenSinceStart ||
        timeWindow === TimeWindowKey.RollingForwardBetween) &&
      !isValidNumberWithinRange(timeWindowValues?.[1])
    ) {
      errors.timeWindowValues = [!!errors.timeWindowValues, true];
    }
  }

  if (simpleCohortTypeKey === SimpleCohortTypeKey.Measure) {
    const measureCohortRowErrors = getMeasureCohortRowErrors(cohortRow);

    return {
      ...errors,
      ...measureCohortRowErrors,
    };
  } else if (simpleCohortTypeKey === SimpleCohortTypeKey.Retention) {
    const retentionCohortRowErrors = getRetentionCohortRowErrors(cohortRow);

    return {
      ...errors,
      ...retentionCohortRowErrors,
    };
  } else if (simpleCohortTypeKey === SimpleCohortTypeKey.Funnel) {
    const funnelCohortRowErrors = getFunnelCohortRowErrors(cohortRow);

    return {
      ...errors,
      ...funnelCohortRowErrors,
    };
  } else if (simpleCohortTypeKey === SimpleCohortTypeKey.Performed) {
    const performedCohortRowErrors = getPerformedCohortRowErrors(cohortRow);

    return {
      ...errors,
      ...performedCohortRowErrors,
    };
  } else if (simpleCohortTypeKey === SimpleCohortTypeKey.Expression) {
    const expressionCohortRowErrors = getExpressionCohortRowErrors(cohortRow);

    return {
      ...errors,
      ...expressionCohortRowErrors,
    };
  } else {
    throw new UnreachableCaseError(simpleCohortTypeKey);
  }
};

export const isValidCohortRow = (cohortRow: ICohortRow): boolean => {
  const errors = getCohortRowErrors(cohortRow);

  return isEmpty(errors);
};
